import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { EmployeesResponse } from './contractsMirror.js';
import type { EmployeesSearch } from './searchParams.js';

/**
 * TanStack Query hook for the COARSE employees endpoint (the strangler-fig +
 * coarse-endpoint proof, CARVE-07). One query key per `{show_archived}` — toggling
 * archived triggers exactly one refetch of `GET /api/v1/employees?show_archived=…`.
 * The SERVER computes the enriched rows + the active/archived counts (22-05); the SPA
 * only renders the returned `EmployeesResponse` (no client re-aggregation, no new N+1 —
 * threat T-22-tenant / CARVE-07). The free-text `?q` filter is applied in-memory in the
 * view over the already-loaded directory, so it is NOT part of the query key.
 *
 * The request goes through `apiFetch` (the only fetch path — `credentials:'include'`
 * + `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is
 * derived server-side from the auth context; the SPA NEVER sends a tenant id param
 * (threat T-22-tenant).
 */

/** Build the stable query key for a filter (one cache entry per archived state). */
export function employeesQueryKey(search: EmployeesSearch): readonly unknown[] {
  return ['employees', search.show_archived ?? false] as const;
}

/** Build the coarse-endpoint request path with the active filter (no tenant param). */
export function employeesRequestPath(search: EmployeesSearch): string {
  return search.show_archived ? '/api/v1/employees?show_archived=1' : '/api/v1/employees';
}

export function useEmployeesQuery(search: EmployeesSearch) {
  return useQuery<EmployeesResponse>({
    queryKey: employeesQueryKey(search),
    queryFn: () => apiFetchJson<EmployeesResponse>(employeesRequestPath(search)),
  });
}
